import axios, { type AxiosError } from 'axios'
import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import type {
    CollectionPointDetail,
    CollectionPointListItem,
    ISODateString,
    CollectionPointWeightingsRespose,
    CollectionPointMixedWeightCategory,
    CollectionPointMixedVolumeCategory,
    CollectionCalendarEntry,
    MutationOptions,
    StatusMutationResponse,
    CollectionPointPublicLink,
    CollectionPointPublicLinkMutationResponse,
    CollectionPointBioWeightCategory,
} from '@/types'
import { isErrorResponse } from '@/helpers'

export function useCollectionPoints(
    municipalityId: number,
) {
    return useQuery({
        queryKey: ['collectionPoints', municipalityId],
        queryFn: async () => {
            const { data } : { data: Array<CollectionPointListItem> } = await axios.get(
                `/api/eoko/household/list?municipality_id=${municipalityId}`,
            )
            return data
        },
        enabled: !!municipalityId,
    })
}

export function useCollectionPoint(collectionPointId) {
    return useQuery({
        queryKey: ['collectionPoint', collectionPointId],
        queryFn: async () => {
            const { data } : { data: CollectionPointDetail } = await axios.get(
                '/api/eoko/household/single?id=' + collectionPointId,
            )
            return data
        },
    })
}

export function useCollectionPointWeightings(
    collectionPointId: number,
    beginsAt: ISODateString,
    endsAt: ISODateString,
) {
    return useQuery({
        queryKey: ['collectionPointWeightings', collectionPointId, 'beginsAt-'+beginsAt, 'endsAt-'+endsAt],
        queryFn: async () => {
            const { data } : { data: CollectionPointWeightingsRespose } = await axios.get(
                `/api/eoko/household/bin_weightings?id=${collectionPointId
                    }&begins_at=${beginsAt
                    }&ends_at=${endsAt}`,
            )
            return data
        },
    })
}

export function useCollectionPointMixedWeightCategory(
    collectionPointId: number,
    year: number,
    disabled?: boolean,
) {
    return useQuery({
        queryKey: ['collectionPointMixedWeightCategory', collectionPointId, year],
        queryFn: async () => {
            const { data } : { data: CollectionPointMixedWeightCategory } = await axios.get(
                `/api/eoko/household/mixed_weight_category_info?household_id=${collectionPointId
                    }&year=${year}`,
            )
            return data
        },
        retry: false,
        enabled: !disabled,
    })
}

export function useCollectionPointMixedVolumeCategory(
    collectionPointId: number,
    year: number,
    disabled?: boolean,
) {
    return useQuery({
        queryKey: ['collectionPointMixedVolumeCategory', collectionPointId, year],
        queryFn: async () => {
            const { data } : { data: CollectionPointMixedVolumeCategory } = await axios.get(
                `/api/eoko/household/mixed_volume_category_info?household_id=${collectionPointId
                    }&year=${year}`,
            )
            return data
        },
        retry: false,
        enabled: !disabled,
    })
}

export function useCollectionPointBioWeightCategory(
    collectionPointId: number,
    year: number,
    disabled?: boolean,
) {
    return useQuery({
        queryKey: ['collectionPointBioWeightCategory', collectionPointId, year],
        queryFn: async () => {
            const { data } : { data: CollectionPointBioWeightCategory } = await axios.get(
                `/api/eoko/household/bio_weight_category_info?household_id=${collectionPointId
                    }&year=${year}`,
            )
            return data
        },
        retry: false,
        enabled: !disabled,
    })
}

export function useCollectionPointCollectionCalendar(
    collectionPointId: number,
    year: number,
    disabled?: boolean,
) {
    return useQuery({
        queryKey: ['collectionPointCollectionCalendar', collectionPointId, 'year-'+year],
        queryFn: async () => {
            const { data } : { data: Array<CollectionCalendarEntry> } = await axios.get(
                `/api/eoko/household/collection_calendar?household_id=${collectionPointId
                    }&year=${year}`,
            )
            return data
        },
        enabled: (!!collectionPointId && !disabled),
    })
}

export const useVerifyCollectionPointByUser = (collectionPointId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/eoko/household/verify_by_user?household_id=${collectionPointId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collectionPoint', collectionPointId] })
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useReimportCollectionPointAdmin = (collectionPointId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/eoko/household/reimport_administrator?household_id=${collectionPointId}`) as Promise<StatusMutationResponse>
        },
        onSuccess: response => {
            const data = response.data
            queryClient.invalidateQueries({ queryKey: ['collectionPoint', collectionPointId] })
            if (data.errors.length === 0) {
                if (data.status_message) {
                    onSuccessCallback(data.status_message)
                } else {
                    onSuccessCallback('Úspěšně aktualizováno.')
                }
            } else {
                onErrorCallback(data.errors.join(', '))
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export function useGetPublicUrl(collectionPointId) {
    return useQuery({
        queryKey: ['collectionPointPublicUrl', collectionPointId],
        queryFn: async () => {
            const { data } : { data: CollectionPointPublicLink } = await axios.get(
                '/api/eoko/household/get_public_url?household_id=' + collectionPointId,
            )
            return data
        },
    })
}

export const useGeneratePublicUrl = (collectionPointId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/eoko/household/generate_public_url?household_id=${collectionPointId}`) as Promise<CollectionPointPublicLinkMutationResponse>
        },
        onSuccess: response => {
            const data = response.data
            queryClient.invalidateQueries({ queryKey: ['collectionPointPublicUrl', collectionPointId] })
            if (data.public_url) {
                navigator.clipboard.writeText(data.public_url)
                onSuccessCallback('Zkopírováno do schránky.')
            } else {
                onErrorCallback('Nepovedlo se vytvořit odkaz.')
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useDisablePublicUrl = (collectionPointId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/eoko/household/disable_public_url?household_id=${collectionPointId}`) as Promise<CollectionPointPublicLinkMutationResponse>
        },
        onSuccess: response => {
            const data = response.data
            queryClient.invalidateQueries({ queryKey: ['collectionPointPublicUrl', collectionPointId] })
            if (data.public_url === false) {
                onSuccessCallback('Odkaz deaktivován.')
            } else {
                onErrorCallback('Nepovedlo se deaktivovat odkaz.')
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

// /api/eoko/household/generate_public_url?household_id=7080
// /api/eoko/household/disable_public_url?household_id=2911
// /api/eoko/household/get_public_url?household_id=10956
